import { selectAccountById } from '@mkp/account/state';
import { UUID } from '@mkp/shared/models';
import { selectRouteParam } from '@mkp/shared/util-router';
import { createFetchStateSelectors, LoadingState } from '@mkp/shared/util-state';
import { Vacancy } from '@mkp/vacancy/models';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { CompanyState, featureKey } from '../reducers';
import * as fromCompany from '../reducers/company-profile.reducer';
import { Location } from '@mkp/location/models';

const { selectLoading, selectLoaded } = createFetchStateSelectors<fromCompany.CompanyState>();
const selectCompanyState = createFeatureSelector<CompanyState>(featureKey);

// EntityAdapter selectors
const adapterSelectors = fromCompany.adapter.getSelectors(selectCompanyState);
export const selectEntities = adapterSelectors.selectEntities;
export const selectAll = adapterSelectors.selectAll;
export const selectIds = adapterSelectors.selectIds;
export const selectTotal = adapterSelectors.selectTotal;

export const selectTotalCount = createSelector(selectCompanyState, (state) => state.totalCount);
export const selectLinks = createSelector(selectCompanyState, (state) => state._links);
export const selectFilter = createSelector(selectCompanyState, (state) => state.filter);
export const selectFetchState = createSelector(selectCompanyState, (state) => state.fetchState);

export const selectCompanyById = (id: string) =>
  createSelector(selectEntities, (companyEntities) => companyEntities[id]);

export const selectCompanyFromRoute = createSelector(
  selectEntities,
  selectRouteParam('companyId'),
  (entities, companyId) => (companyId ? entities[companyId] : undefined)
);

export const selectCompanyProfiles = createSelector(selectAll, (companies) => companies);

export const selectCompanyProfilesLoaded = createSelector(selectCompanyState, (state) =>
  selectLoaded(state)
);

export const selectCompanyProfilesLoading = createSelector(selectCompanyState, (state) =>
  selectLoading(state)
);

export const selectSelectedCompanyId = createSelector(
  selectCompanyState,
  ({ selectedId }) => selectedId
);

export const selectSelectedCompanyProfile = createSelector(
  selectEntities,
  selectSelectedCompanyId,
  (entities, selectedId) => (selectedId ? entities[selectedId] : undefined)
);

// Compatibility selectors (for company.selectors.ts consumers)
export const selectCompaniesDictionary = selectEntities;
export const selectCompaniesIds = selectIds;
export const selectCompaniesFetchState = selectFetchState;
export const selectCompanies = selectAll;

export const selectDefaultCompany = createSelector(selectAll, (entities) => entities[0]);

export const selectSelectedCompany = createSelector(
  selectEntities,
  selectSelectedCompanyId,
  selectAll,
  (entitiesDict, selectedId, companiesArray) => {
    if (!entitiesDict || !companiesArray) return null;
    return selectedId ? entitiesDict[selectedId] : companiesArray[0] || null;
  }
);

export const selectCompanyIsLoading = createSelector(
  selectFetchState,
  (fetchState) => fetchState === LoadingState.LOADING
);

export const selectCompanyLocationsFromRoute = createSelector(
  selectCompanyFromRoute,
  (company): Location[] => company?.locations ?? []
);

const selectCompaniesByAccountId = (accountId: string) =>
  createSelector(selectAccountById(accountId), selectCompanyProfiles, (account, companies) =>
    companies.filter(({ legalEntityId }) => account?.legalEntity.id === legalEntityId)
  );

export const selectFirstCompanyByAccountId = (accountId: string) =>
  createSelector(selectCompaniesByAccountId(accountId), (companies) => companies[0]);

export const selectCompanyVideosOfFirstCompany = createSelector(
  selectCompanyProfiles,
  (companies) => companies[0]?.videos ?? []
);

export const selectDefaultCompanyLogoUrl = createSelector(
  selectEntities,
  selectSelectedCompanyId,
  (entities, selectedId) => (selectedId ? entities[selectedId]?.logoMediaApiId : undefined)
);

export const selectBenefitsByCompanyId = (id: UUID) =>
  createSelector(selectCompanyById(id), (company): UUID[] => (company ? company['benefits'] : []));

export const selectCompanyByVacancy = (vacancy: Vacancy) =>
  createSelector(selectCompanyProfiles, (companies) => {
    return companies.find((company) =>
      company.locations?.find((location) => location.id === vacancy.locationId)
    );
  });
